import * as fs from 'node:fs';
import * as path from 'node:path';
import {
  LEGACY_WALK_MAX_DEPTH,
  shouldSkipDiskWalkEntry,
  type DiskWalkResult,
  type DiskWalkState,
} from './disk-walk-constants.js';
import { isGitDir, yieldDuringDiskWalk } from './disk-walk-utils.js';

/** Depth-first: git repo roots under `rootPath` (does not descend past a repo root). */
export async function collectGitRepoRootsUnderDirectory(rootPath: string): Promise<DiskWalkResult> {
  const out: string[] = [];
  const state: DiskWalkState = { entries: 0 };

  const walk = async (dir: string): Promise<void> => {
    if (!(await yieldDuringDiskWalk(state))) return;
    if (await isGitDir(dir)) {
      out.push(path.resolve(dir));
      return;
    }
    let entries: fs.Dirent[];
    try {
      entries = await fs.promises.readdir(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const e of entries) {
      if (shouldSkipDiskWalkEntry(e.name)) continue;
      const full = path.join(dir, e.name);
      if (!e.isDirectory()) continue;
      await walk(full);
    }
  };

  await walk(path.resolve(rootPath));
  return { paths: [...new Set(out)], entriesVisited: state.entries };
}

/** Depth-first: directories named `sessionId` that look like git checkouts (legacy layout). */
export async function collectWorktreeRootsNamed(
  root: string,
  sessionId: string,
  maxDepth: number = LEGACY_WALK_MAX_DEPTH,
): Promise<DiskWalkResult> {
  const out: string[] = [];
  const state: DiskWalkState = { entries: 0 };

  const walk = async (dir: string, depth: number): Promise<void> => {
    if (depth > maxDepth) return;
    if (!(await yieldDuringDiskWalk(state))) return;
    let entries: fs.Dirent[];
    try {
      entries = await fs.promises.readdir(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const e of entries) {
      if (shouldSkipDiskWalkEntry(e.name)) continue;
      const full = path.join(dir, e.name);
      if (!e.isDirectory()) continue;
      if (e.name === sessionId) {
        if (await isGitDir(full)) out.push(path.resolve(full));
        continue;
      }
      // Legacy checkout is `{repoRel}/{sessionId}` — never inside another repo's source tree.
      if (await isGitDir(full)) continue;
      await walk(full, depth + 1);
    }
  };

  await walk(root, 0);
  return { paths: out, entriesVisited: state.entries };
}
